import type { IntlShape } from 'react-intl'

import { FormattedMessage, showSnackbar } from '@b-stock/fe-core/bstock-react'
import type { PayMethod } from '@b-stock/payments-methods-api-client'
import { PayMethodTypeEnum } from '@b-stock/payments-methods-api-client'

import { getDetails, getOwner, getTypeLabel } from '../utils'

// Shared by Checkout, BidNow and BuyNow payment-add flows; the literal string
// keeps a single i18n source-of-truth for the "method added" toast copy.
const SUCCESS_CONTENT_ID = 'CheckoutModal.add.paymentMethod.success.content'

type MessageDescriptor = {
  id: string
  values: Record<string, string | number>
}

const resolveLast4 = (method: PayMethod): string | undefined => {
  const details = getDetails(method)
  const last4 = details.last4 || details.us_bank_account?.last4
  return last4 && last4.trim() ? last4 : undefined
}

const resolveName = (method: PayMethod): string => {
  // Balance CC: hide account-owner name (not the cardholder) per existing UX
  if (method.type === PayMethodTypeEnum.GetbalanceCc) return 'empty'
  const name = getOwner(method)
  return name && name !== 'Unknown' && name.trim() ? name : 'empty'
}

/**
 * Builds the i18n descriptor for the "payment method added" snackbar.
 * Returns null if the method has no resolvable last4 (caller should skip the toast).
 */
const resolvePaymentMethodAddedContent = (
  newMethod: PayMethod
): MessageDescriptor | null => {
  const last4 = resolveLast4(newMethod)
  if (!last4) return null

  return {
    id: SUCCESS_CONTENT_ID,
    values: {
      type: getTypeLabel(newMethod),
      last4,
      name: resolveName(newMethod),
    },
  }
}

/**
 * Builds the exact string rendered by `showPaymentMethodAddedSnackbar`.
 * Used for analytics (Toast_View event) so tracked copy stays in sync with UI.
 * Returns an empty string when the method has no resolvable details.
 */
export const getPaymentMethodAddedMessage = (
  intl: IntlShape,
  newMethod: PayMethod
): string => {
  const descriptor = resolvePaymentMethodAddedContent(newMethod)
  if (!descriptor) return ''
  return intl.formatMessage({ id: descriptor.id }, descriptor.values)
}

/**
 * Shows a success snackbar for a newly added payment method.
 * No-op when the method has no resolvable details (caller should treat as warning).
 */
export const showPaymentMethodAddedSnackbar = (
  newMethod: PayMethod,
  options: { autoClose?: boolean } = {}
): boolean => {
  const descriptor = resolvePaymentMethodAddedContent(newMethod)
  if (!descriptor) return false

  showSnackbar({
    content: <FormattedMessage id={descriptor.id} values={descriptor.values} />,
    options: { type: 'success', ...options },
  })
  return true
}
